import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardStats } from '../../services/api';
import { Store, Package, TrendingUp, DollarSign, ShoppingCart, QrCode, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const DashboardHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    branches: 0,
    products: 0,
    sales: 0,
    revenue: 0,
    pos_sales: 0,
    pos_revenue: 0,
    marketplace_sales: 0,
    marketplace_revenue: 0,
    menu_digital_sales: 0,
    menu_digital_revenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [user]);

  const loadStats = async () => {
    if (!user?.business_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await getDashboardStats(user.business_id);
      setStats({
        branches: response.data.branches,
        products: response.data.products,
        sales: response.data.sales,
        revenue: response.data.revenue,
        pos_sales: response.data.pos_sales || 0,
        pos_revenue: response.data.pos_revenue || 0,
        marketplace_sales: response.data.marketplace_sales || 0,
        marketplace_revenue: response.data.marketplace_revenue || 0,
        menu_digital_sales: response.data.menu_digital_sales || 0,
        menu_digital_revenue: response.data.menu_digital_revenue || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const fmtMoney = (v) => `$${v.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  const statCards = [
    {
      icon: Store,
      label: 'Sucursales',
      value: stats.branches,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Package,
      label: 'Productos',
      value: stats.products,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: TrendingUp,
      label: 'Ventas Totales',
      value: stats.sales,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: DollarSign,
      label: 'Ingresos Totales',
      value: fmtMoney(stats.revenue),
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
    },
  ];

  const salesBreakdown = [
    {
      icon: Monitor,
      label: 'Ventas POS',
      sales: stats.pos_sales,
      revenue: stats.pos_revenue,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
    },
    {
      icon: ShoppingCart,
      label: 'Marketplace',
      sales: stats.marketplace_sales,
      revenue: stats.marketplace_revenue,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
    },
    {
      icon: QrCode,
      label: 'Menú Digital',
      sales: stats.menu_digital_sales,
      revenue: stats.menu_digital_revenue,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
    },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
            Bienvenido, {user?.full_name}
          </h1>
          <p className="text-slate-600 mt-2">Resumen de tu negocio</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                data-testid={`stat-card-${stat.label.toLowerCase()}`}
                className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-4 rounded-lg`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sales Breakdown by Source */}
        {(stats.pos_sales > 0 || stats.marketplace_sales > 0 || stats.menu_digital_sales > 0) && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Desglose de Ventas por Canal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {salesBreakdown.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`bg-white p-6 rounded-xl border ${item.borderColor} shadow-sm hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`${item.bgColor} ${item.color} p-3 rounded-lg`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{item.label}</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Ventas</span>
                        <span className="text-lg font-bold text-slate-900">{item.sales}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Ingresos</span>
                        <span className="text-lg font-bold text-slate-900">{fmtMoney(item.revenue)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Acciones Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              data-testid="quick-action-add-product"
              onClick={() => window.location.href = '/dashboard/productos'}
              className="px-6 py-4 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 font-medium transition-colors text-left"
            >
              + Agregar Producto
            </button>
            <button
              data-testid="quick-action-add-branch"
              onClick={() => window.location.href = '/dashboard/sucursales'}
              className="px-6 py-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium transition-colors text-left"
            >
              + Crear Sucursal
            </button>
            <button
              data-testid="quick-action-view-reports"
              onClick={() => window.location.href = '/dashboard/reportes'}
              className="px-6 py-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 font-medium transition-colors text-left"
            >
              Ver Reportes
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardHome;